// FudiTap guest service requests (Step 3): waiter / bill / help through the real API
// (nfc-panel docs/architecture/FUDITAP_GUEST_ORDER_CONTRACT.md, "Guest service requests").
//
// Sends only { cardCode | tableToken, idempotencyKey, type, reason? }. The table
// comes from the server. One idempotency key is kept per (entry, type, reason)
// until a definitive answer, so a retry after a network failure never creates a
// second request; the server also returns the live request on re-taps.
import { entryBody, entryFrom, entryQuery } from "@/lib/guestEntry";
import { newIdempotencyKey } from "@/lib/guestOrder";

export const SERVICE_ATTEMPT_KEY = "fuditap_service_attempt";
const TYPES = ["WAITER", "BILL", "HELP"];
const REASONS = {
  WAITER: ["water", "cutlery", "napkins", "sauce", "cleaning", "order_help", "other"],
  BILL: ["cash", "card", "split"],
  HELP: ["other"],
};
const STATUSES = ["OPEN", "CLAIMED", "RESOLVED", "CANCELED"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

// Existing PWA waiter choices -> contract (type, reason).
export const WAITER_CHOICES = {
  waiterOrder: ["WAITER", "order_help"], waiterWater: ["WAITER", "water"], waiterNapkin: ["WAITER", "napkins"],
  waiterCutlery: ["WAITER", "cutlery"], waiterSauce: ["WAITER", "sauce"], waiterClean: ["WAITER", "cleaning"],
  waiterRequest: ["HELP", "other"], waiterOther: ["WAITER", "other"],
};

export function parseServiceRequest(json) {
  if (!json || typeof json !== "object" || typeof json.requestToken !== "string" || !UUID.test(json.requestToken) ||
      !TYPES.includes(json.type) || !(json.reason === null || (REASONS[json.type] || []).includes(json.reason)) ||
      !STATUSES.includes(json.status) || typeof json.createdAt !== "string" || !ISO.test(json.createdAt) ||
      typeof json.updatedAt !== "string" || !ISO.test(json.updatedAt)) return null;
  return { requestToken: json.requestToken.toLowerCase(), type: json.type, reason: json.reason, status: json.status,
    createdAt: json.createdAt, updatedAt: json.updatedAt };
}

const ERRORS = {
  REQUEST_INVALID: "invalid", REQUEST_TOO_LARGE: "invalid", MENU_NOT_FOUND: "not_found", TABLE_REQUIRED: "table_required",
  TABLE_SESSION_REQUIRED: "table_session_required", RATE_LIMITED: "rate_limited",
};

function readAttempt(storage) {
  try { return JSON.parse(storage?.getItem(SERVICE_ATTEMPT_KEY) || "null"); } catch { return null; }
}
function writeAttempt(storage, value) {
  try { if (value) storage?.setItem(SERVICE_ATTEMPT_KEY, JSON.stringify(value)); else storage?.removeItem(SERVICE_ATTEMPT_KEY); } catch { /* memory only */ }
}

async function request(url, init, fetchImpl, timeoutMs) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    return await fetchImpl(url, { ...init, credentials: "omit", cache: "no-store", redirect: "error", referrerPolicy: "no-referrer", signal: controller?.signal });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Returns { ok: true, request } or { ok: false, reason }. Never pretends success. */
export async function createServiceRequest(rawEntry, type, reason = null, {
  baseUrl = "", fetchImpl = globalThis.fetch, storage, newKey, timeoutMs = 10000,
} = {}) {
  const entry = entryFrom(rawEntry);
  if (!entry) return { ok: false, reason: "not_found" };
  if (!TYPES.includes(type) || !(reason === null || REASONS[type].includes(reason))) return { ok: false, reason: "invalid" };
  const fingerprint = JSON.stringify({ entry, type, reason });
  let idempotencyKey;
  const prev = readAttempt(storage);
  if (prev && prev.fingerprint === fingerprint && typeof prev.key === "string" && UUID.test(prev.key)) {
    idempotencyKey = prev.key;
  } else {
    try { idempotencyKey = (newKey || (() => newIdempotencyKey()))(); } catch { return { ok: false, reason: "unavailable" }; }
    writeAttempt(storage, { fingerprint, key: idempotencyKey });
  }
  const body = { ...entryBody(entry), idempotencyKey, type, ...(reason ? { reason } : {}) };
  let response;
  try {
    response = await request(`${baseUrl}/api/guest/service-requests`, {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body),
    }, fetchImpl, timeoutMs);
  } catch {
    return { ok: false, reason: "network" };   // may exist: keep the key for the retry
  }
  let json = null;
  try { json = await response.json(); } catch { /* handled below */ }
  if (response.ok) {
    const parsed = parseServiceRequest(json);
    if (!parsed) return { ok: false, reason: "unavailable" };
    writeAttempt(storage, null);
    return { ok: true, request: parsed };
  }
  return { ok: false, reason: ERRORS[json?.error] || "unavailable" };
}

export async function fetchServiceRequestStatus(rawEntry, requestToken, {
  baseUrl = "", fetchImpl = globalThis.fetch, timeoutMs = 10000,
} = {}) {
  const entry = entryFrom(rawEntry);
  if (!entry || typeof requestToken !== "string" || !UUID.test(requestToken)) return { ok: false, reason: "not_found" };
  let response;
  try {
    response = await request(`${baseUrl}/api/guest/service-requests/status?${entryQuery(entry)}&request=${encodeURIComponent(requestToken)}`,
      { method: "GET", headers: { Accept: "application/json" } }, fetchImpl, timeoutMs);
  } catch {
    return { ok: false, reason: "network" };
  }
  if (response.status === 404) return { ok: false, reason: "not_found" };
  if (!response.ok) return { ok: false, reason: "unavailable" };
  let json = null;
  try { json = await response.json(); } catch { /* handled below */ }
  const parsed = parseServiceRequest(json);
  return parsed ? { ok: true, request: parsed } : { ok: false, reason: "unavailable" };
}
